import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import storageService from '@app/services/storageService'

import { getMappedArray } from '@app/utils/arrays'

import {
  getInitialModifiedColumns,
  getDropdownItemsFromStorage,
  getModifiedColumnsByDropdownItems,
  convertColumnsToDropdownItems
} from './utils'

import type { Column } from '@app/components/base/Table/types'
import type { UseTableSettingReturn } from './types'
import type { DropdownData, DropdownMenuItem } from '@app/types/dropdown'

export const useTableSetting = <ColumnType = unknown>(
  settingKey: string,
  columns: Array<Column<ColumnType>>
): UseTableSettingReturn<ColumnType> => {
  const [modifiedColumns, setModifiedColumns] = useState<Array<Column<ColumnType>>>(() =>
    getInitialModifiedColumns(settingKey, columns)
  )

  const items = useMemo<DropdownMenuItem[]>(
    () => getDropdownItemsFromStorage(settingKey) ?? convertColumnsToDropdownItems(columns as Column[]),
    []
  )

  const defaultItems = useMemo<DropdownMenuItem[]>(() => convertColumnsToDropdownItems(columns as Column[]), [])
  const columnsMapRef = useRef<Record<string, Column<ColumnType>>>(getMappedArray(columns, 'key'))

  const handleModify = useCallback((data: DropdownData[], items: DropdownMenuItem[]): void => {
    storageService.setItem(settingKey, JSON.stringify(items))
    setModifiedColumns(getModifiedColumnsByDropdownItems(columnsMapRef.current, data))
  }, [])

  useEffect(() => {
    columnsMapRef.current = getMappedArray(columns, 'key')
    setModifiedColumns(getInitialModifiedColumns(settingKey, columns))
  }, [columns])

  return {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify: handleModify
  }
}
